import type { GameState, PlayerState } from '../shared/types';
import { Terrain } from '../shared/terrain';
import type { BulletAsset, CharacterAsset, GameAssets } from './renderer';
import classicAim from '../shared/classic-aim.json';
import attackAssets from '../shared/character-attack-assets.json';
import feedbackAssets from '../shared/character-feedback-assets.json';
import deathAssets from '../shared/character-death-assets.json';
import resultAssets from '../shared/battle-result-assets.json';
import classicUi from '../shared/classic-ui.json';
import classicGauge from '../shared/classic-gauge.json';
import supplement from '../shared/classic-ui-supplement.json';
import { classicToolsAssetPaths } from './classic-tools';
import { roomAssetKey } from '../shared/room-assets-key';
import { assetCacheKey, assetSourceUrl, bindAssetImage } from './hd-assets';
import type { ResourcePackLoader } from './resource-pack-loader';

export { roomAssetKey } from '../shared/room-assets-key';

type RoomAssetsState = Pick<GameState, 'mapId' | 'players'>;
export interface BattleAssetPlan { images: string[]; masks: string[]; valid: boolean }

export const MAX_CONCURRENT_ASSET_LOADS = 12;
export const ASSET_LOAD_TIMEOUT_MS = 60000;

export function withCharacterFeedback(assets: GameAssets): GameAssets {
  return { ...assets, characters: assets.characters.map((character) => {
    const extra = (feedbackAssets.characters as Record<string, Partial<CharacterAsset>>)[String(character.id)];
    return { ...character, ...extra, animationTimelines: { ...character.animationTimelines, ...extra?.animationTimelines } };
  }) };
}

/** The room and battle renderer use this same complete critical inventory. */
export function collectBattleAssets(assets: GameAssets, state: RoomAssetsState): BattleAssetPlan {
  const images = new Set<string>(), masks = new Set<string>();
  const map = assets.maps.find((entry) => String(entry.id) === String(state.mapId)) ?? assets.maps[0];
  let valid = Boolean(map && assets.characters.length);
  if (map) { images.add(map.background); images.add(map.foreground); if (map.mask) masks.add(map.mask); }
  images.add(classicAim.assets.background.path); images.add(classicAim.assets.hand.path);
  for (const path of classicToolsAssetPaths) images.add(path);
  for (const movie of Object.values(resultAssets.assets)) images.add(movie.path);
  for (const item of Object.values(supplement.assets)) if ('path' in item) images.add(item.path);
  for (const item of [...Object.values(classicUi.assets), ...Object.values(classicGauge.assets)]) {
    if ('path' in item) images.add(item.path);
    if ('frames' in item) for (const path of item.frames) images.add(path);
    if ('pieces' in item) for (const piece of Object.values(item.pieces)) images.add(piece.path);
  }
  for (const path of Object.values(assets.ui ?? {})) if (path.startsWith('/game/')) images.add(path);
  const addBall = (ball: BulletAsset) => {
    for (const path of [...ball.projectile, ...ball.explosion]) images.add(path);
    if (ball.crater) images.add(ball.crater);
    if (ball.craterMask) masks.add(ball.craterMask);
  };
  for (const character of assets.characters) {
      if (character.roomPortrait) images.add(character.roomPortrait);
      const extra = (feedbackAssets.characters as Record<string, { handclip: string[]; cry: string[]; hit: string[] }>)[String(character.id)];
      const idle = Array.isArray(character.idle) ? character.idle : [character.idle];
      for (const path of [...idle, ...character.walk, ...character.shot, ...character.throw ?? [], ...character.showgun ?? [],
        ...character.showthrows ?? [], ...character.hidegun ?? [], ...character.hidethrows ?? [],
        ...character.handclip ?? extra?.handclip ?? [], ...character.cry ?? extra?.cry ?? [], ...character.hit ?? extra?.hit ?? []]) images.add(path);
  }
  for (const weaponId of new Set(state.players.map((player) => player.weaponId))) {
    const weapon = assets.weapons.find((entry) => String(entry.id) === String(weaponId));
    if (!weapon) { valid = false; continue; }
    images.add(weapon.icon); images.add(weapon.held); if (weapon.heldSheet) images.add(weapon.heldSheet);
    for (const ball of [weapon.normal, weapon.special]) addBall(ball);
    const movies = (attackAssets.weapons as Record<string, { normal: { frames: string[] }; special: { frames: string[] } }>)[String(weapon.id)];
    if (movies) for (const movie of Object.values(movies)) for (const path of movie.frames) images.add(path);
  }
  for (const ball of Object.values(assets.actions ?? {})) if (ball) addBall(ball);
  for (const movie of Object.values(attackAssets.actions)) for (const path of movie.frames) images.add(path);
  for (const effect of Object.values(feedbackAssets.effects)) for (const path of effect.frames) images.add(path);
  for (const character of Object.values(deathAssets.characters)) for (const path of character.soul) images.add(path);
  for (const effect of Object.values(deathAssets.effects)) {
    images.add(effect.path);
    if ('frames' in effect) for (const path of effect.frames) images.add(path);
  }
  for (const prop of assets.props ?? []) images.add(prop.icon);
  return { images: [...images].filter(Boolean), masks: [...masks].filter(Boolean), valid };
}

export class BattleAssetLoader {
  readonly assets: GameAssets;
  onReady?: (key: string, ready: boolean) => void;
  onProgress?: () => void;
  private images = new Map<string, HTMLImageElement>();
  private terrains = new Map<string, Terrain>();
  private imageLoads = new Map<string, Promise<boolean>>();
  private maskLoads = new Map<string, Promise<boolean>>();
  private rooms = new Map<string, Promise<boolean>>();
  private plans = new Map<string, BattleAssetPlan>();
  private readyKeys = new Set<string>();
  private failed = new Set<string>();
  private activeLoads = 0;
  private queuedLoads: Array<() => void> = [];
  private allPlan: BattleAssetPlan;
  private allLoading: Promise<boolean> | null = null;

  constructor(assets: GameAssets, private packs?: ResourcePackLoader) {
    this.assets = withCharacterFeedback(assets);
    const players = assets.weapons.map(weapon => ({ weaponId: String(weapon.id) }) as PlayerState);
    const plans = assets.maps.map(map => collectBattleAssets(this.assets, { mapId: String(map.id), players }));
    this.allPlan = { valid: plans.length > 0 && plans.every(plan => plan.valid),
      images: [...new Set(plans.flatMap(plan => plan.images))], masks: [...new Set(plans.flatMap(plan => plan.masks))] };
  }

  statusAll() {
    const plan = this.allPlan;
    const pending = [...plan.images.filter(path => !this.getImage(path)), ...plan.masks.filter(path => !this.terrains.has(path))];
    return { ready: plan.valid && pending.length === 0, total: plan.images.length + plan.masks.length,
      loaded: plan.images.length + plan.masks.length - pending.length, pending, failed: pending.filter(path => this.resourceFailed(path)),
      activeLoads: this.activeLoads, queuedLoads: this.queuedLoads.length, delivery: this.packs?.status() ?? null };
  }

  /** One entry barrier covers every map and weapon, including decoded frames. */
  warmAll(): Promise<boolean> {
    if (this.statusAll().ready) return Promise.resolve(true);
    if (this.allLoading) return this.allLoading;
    const plan = this.allPlan;
    // Pack transfers have their own quota and continue while frames decode.
    if (this.packs) void this.packs.preload([...plan.images.map(assetSourceUrl), ...plan.masks]);
    const loading = Promise.all([...plan.images.map(path => this.loadImage(path)), ...plan.masks.map(path => this.loadTerrain(path))])
      .then(loaded => plan.valid && loaded.every(Boolean)).finally(() => { this.allLoading = null; this.onProgress?.(); });
    this.allLoading = loading;
    return loading;
  }

  getImage(path: string): HTMLImageElement | null { return this.images.get(assetCacheKey(path)) ?? null; }
  getTerrain(path: string): Terrain | null { return this.terrains.get(path) ?? null; }
  imageReady(path: string): Promise<HTMLImageElement | null> { return this.loadImage(path).then((ready) => ready ? this.getImage(path) : null); }
  terrainReady(path: string): Promise<Terrain | null> { return this.loadTerrain(path).then((ready) => ready ? this.getTerrain(path) : null); }

  private planFor(state: RoomAssetsState): BattleAssetPlan {
    const key = roomAssetKey(state);
    if (!this.plans.has(key)) this.plans.set(key, collectBattleAssets(this.assets, state));
    return this.plans.get(key)!;
  }

  readyFor(state: RoomAssetsState): boolean {
    const key = roomAssetKey(state); if (this.readyKeys.has(key)) return true;
    const plan = this.planFor(state);
    const ready = plan.valid && plan.images.every((path) => Boolean(this.getImage(path))) && plan.masks.every((path) => this.terrains.has(path));
    if (ready) this.readyKeys.add(key);
    return ready;
  }

  statusFor(state: RoomAssetsState) {
    const plan = this.planFor(state);
    const pending = [...plan.images.filter((path) => !this.getImage(path)), ...plan.masks.filter((path) => !this.terrains.has(path))];
    return { key: roomAssetKey(state), ready: this.readyFor(state), total: plan.images.length + plan.masks.length,
      loaded: plan.images.length + plan.masks.length - pending.length, pending, failed: pending.filter((path) => this.resourceFailed(path)),
      activeLoads: this.activeLoads, queuedLoads: this.queuedLoads.length };
  }

  warm(state: RoomAssetsState): Promise<boolean> {
    const key = roomAssetKey(state);
    if (this.readyFor(state)) return Promise.resolve(true);
    const pending = this.rooms.get(key); if (pending) return pending;
    const plan = this.planFor(state);
    const loading = Promise.all([...plan.images.map((path) => this.loadImage(path)), ...plan.masks.map((path) => this.loadTerrain(path))])
      .then((loaded) => plan.valid && loaded.every(Boolean))
      .then((ready) => { this.rooms.delete(key); if (ready) this.readyKeys.add(key); this.onReady?.(key, ready); return ready; });
    this.rooms.set(key, loading);
    return loading;
  }

  /** Packed transfers finish before taking a decode slot; legacy requests keep
   * their bounded transfer/decode quota. Queued assets have no running timeout. */
  private schedule<T>(load: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queuedLoads.push(() => {
        this.activeLoads++;
        void Promise.resolve().then(load).then(resolve, reject).finally(() => {
          this.activeLoads--;
          this.drainLoads();
        });
      });
      this.drainLoads();
    });
  }

  private drainLoads(): void {
    while (this.activeLoads < MAX_CONCURRENT_ASSET_LOADS && this.queuedLoads.length) this.queuedLoads.shift()!();
  }

  private resourceFailed(path: string): boolean { return this.failed.has(path) || this.failed.has(assetCacheKey(path)); }

  private loadImage(path: string): Promise<boolean> {
    const key = assetCacheKey(path);
    if (this.images.has(key)) return Promise.resolve(true);
    const existing = this.imageLoads.get(key); if (existing) return existing;
    this.failed.delete(key);
    const transfer = this.packs?.has(assetSourceUrl(path)) ? this.packs.file(assetSourceUrl(path)) : Promise.resolve(null);
    const loading = transfer.then(() => this.schedule(() => new Promise<boolean>((resolve) => {
      const image = new Image();
      let finished = false;
      const finish = (ready: boolean) => {
        if (finished) return; finished = true;
        clearTimeout(timeout); image.onload = null; image.onerror = null;
        if (ready) this.images.set(key, image);
        else { this.failed.add(key); image.removeAttribute('src'); }
        resolve(ready);
      };
      const timeout = setTimeout(() => finish(false), ASSET_LOAD_TIMEOUT_MS);
      image.onload = () => {
        if (!image.naturalWidth) { finish(false); return; }
        image.decode().then(() => finish(true), () => finish(false));
      };
      image.onerror = () => finish(false);
      bindAssetImage(image, path);
    }))).catch(() => { this.failed.add(key); return false; })
      .finally(() => { this.imageLoads.delete(key); this.onProgress?.(); });
    this.imageLoads.set(key, loading);
    return loading;
  }

  private loadTerrain(path: string): Promise<boolean> {
    if (this.terrains.has(path)) return Promise.resolve(true);
    const existing = this.maskLoads.get(path); if (existing) return existing;
    this.failed.delete(path);
    const transfer = this.packs?.has(path) ? this.packs.file(path) : Promise.resolve(null);
    const loading = transfer.then(file => this.schedule(async () => {
      if (file) {
        this.terrains.set(path, Terrain.fromBuffer(await file.arrayBuffer())); return true;
      }
      return fetch(path, { signal: AbortSignal.timeout(ASSET_LOAD_TIMEOUT_MS) }).then(async (response) => {
      if (!response.ok) throw new Error('Missing collision mask');
      this.terrains.set(path, Terrain.fromBuffer(await response.arrayBuffer())); return true;
      });
    })).catch(() => { this.failed.add(path); return false; })
      .finally(() => { this.maskLoads.delete(path); this.onProgress?.(); });
    this.maskLoads.set(path, loading);
    return loading;
  }
}
